import type { NamedRef } from '@/types/api'

export type FeeFrequency = 'monthly' | 'termly' | 'yearly' | 'one_time'
export type DiscountType = 'percentage' | 'fixed'
export type PaymentMethod = 'cash' | 'bank_transfer' | 'cheque' | 'card' | 'mobile_money'
export type InvoiceStatus = 'unpaid' | 'partial' | 'paid' | 'overdue' | 'cancelled'

export const FEE_FREQUENCIES: FeeFrequency[] = ['monthly', 'termly', 'yearly', 'one_time']
export const PAYMENT_METHODS: PaymentMethod[] = ['cash', 'bank_transfer', 'cheque', 'card', 'mobile_money']
export const INVOICE_STATUSES: InvoiceStatus[] = ['unpaid', 'partial', 'overdue', 'paid', 'cancelled']

export interface FeeCategory {
  id: number
  name: string
  isActive: boolean
  itemCount: number
}

export interface FeeItem {
  id: number
  name: string
  category: NamedRef
  academicYear: NamedRef
  schoolClass: NamedRef | null
  amount: string
  frequency: FeeFrequency
  dueDay: number | null
  isMandatory: boolean
  isActive: boolean
  invoicedCount: number
}

export interface FeeDiscount {
  id: number
  name: string
  type: DiscountType
  value: string
  maxAmount: string | null
  isActive: boolean
  studentCount: number
}

export interface Invoice {
  id: number
  number: string
  student: { id: number; name: string; studentCode: string; className: string | null }
  academicYearId: number
  academicYear: string
  period: string
  periodLabel: string
  issueDate: string
  dueDate: string
  currency: string
  subtotal: string
  discountTotal: string
  total: string
  amountPaid: string
  outstanding: string
  status: InvoiceStatus
  isOverdue: boolean
  createdAt: string
}

export interface InvoiceLine {
  id: number
  feeItemId: number | null
  name: string
  category: string
  frequency: FeeFrequency
  amount: string
  discountName: string | null
  discountAmount: string
  netAmount: string
}

export interface Payment {
  id: number
  receiptNumber: string
  invoice: { id: number; number: string; periodLabel: string }
  student: { id: number; name: string; studentCode: string }
  amount: string
  currency: string
  method: PaymentMethod
  reference: string | null
  paidOn: string
  receivedBy: string | null
  note: string | null
  isReversed: boolean
  reversedAt: string | null
  reversedBy: string | null
  reversalReason: string | null
  createdAt: string
}

export interface InvoiceDetail {
  invoice: Invoice
  lines: InvoiceLine[]
  payments: Payment[]
  issuedBy: string | null
  cancellation: { at: string; by: string | null; reason: string | null } | null
  canCancel: boolean
}

export interface Receipt {
  payment: Payment
  invoice: Invoice
  school: {
    name: string
    address: string | null
    phone: string | null
    email: string | null
    logoUrl: string | null
  }
  student: {
    id: number
    name: string
    studentCode: string
    admissionNumber: string
    className: string | null
    sectionName: string | null
  }
}

export interface GenerationResult {
  dryRun: boolean
  academicYear: NamedRef
  schoolClass: NamedRef | null
  period: string
  periodLabel: string
  issueDate: string
  currency: string
  studentCount: number
  invoiceCount: number
  lineCount: number
  alreadyBilledCount: number
  noItemsCount: number
  total: string
  items: {
    id: number
    name: string
    category: string
    frequency: FeeFrequency
    amount: string
    className: string | null
  }[]
  invoiceIds: number[]
}

export interface BalanceSummary {
  totalInvoiced: string
  totalPaid: string
  outstanding: string
  overdue: string
  invoiceCount: number
  openInvoiceCount: number
}

export interface LedgerEntry {
  date: string
  type: 'invoice' | 'payment' | 'reversal' | 'cancellation'
  reference: string
  description: string
  debit: string
  credit: string
  balance: string
  invoiceId: number | null
  paymentId: number | null
}

export interface Ledger {
  student: {
    id: number
    name: string
    studentCode: string
    className: string | null
    sectionName: string | null
    status: string
  }
  discount: { id: number; name: string; type: DiscountType; value: string; isActive: boolean } | null
  currency: string
  summary: BalanceSummary
  entries: LedgerEntry[]
}

export interface FamilyFees {
  student: { id: number; name: string; studentCode: string }
  currency: string
  summary: BalanceSummary
  invoices: (Invoice & { lines: InvoiceLine[] })[]
  payments: Omit<Payment, 'receivedBy' | 'reversedBy' | 'note'>[]
}

export interface FinanceOverview {
  collectedThisMonth: string
  outstandingTotal: string
  overdueCount: number
  overdueTotal: string
  invoicesThisMonth: number
  collectionsByMonth: { month: string; label: string; value: number }[]
}

export interface OutstandingByClass {
  classId: number
  className: string
  invoiceCount: number
  studentCount: number
  totalDue: string
  overdueAmount: string
}

export interface Collections {
  from: string
  to: string
  byDay: { date: string; count: number; total: string }[]
  byMethod: { method: PaymentMethod; count: number; total: string }[]
  count: number
  total: string
}

export interface Debtor {
  studentId: number
  name: string
  studentCode: string
  className: string | null
  invoiceCount: number
  outstanding: string
  oldestDueDate: string
}

export interface InvoiceFilters {
  status?: string
  classId?: string
  academicYearId?: string
  period?: string
  studentId?: string
}

export interface PaymentFilters {
  method?: string
  from?: string
  to?: string
  state?: string
}
